import React, { useCallback, useMemo } from "react";
import { NavLink } from "react-router-dom";
import {
  FiUsers,
  FiBarChart2,
  FiBookOpen,
  FiGlobe,
  FiMapPin,
} from "react-icons/fi";
import logo from "../assets/logo.png";
import { PAGE_PATHS } from "../pages/page-paths";
import { services } from "../services";
import { useAuth } from "../utils/hooks/useAuth";
import { ADMIN_ROLE, AdminRole, getRoleLabel } from "../utils/roles";

type MenuItem = {
  label: string;
  path: string;
  roles: AdminRole[];
  icon: React.ComponentType<{ className?: string }>;
};

const MENU_ITEMS: MenuItem[] = [
  {
    label: "Country Stats",
    path: PAGE_PATHS.COUNTRY_STATS,
    roles: [
      ADMIN_ROLE.SUPER_ADMIN,
      ADMIN_ROLE.CENTRAL_ADMIN,
      ADMIN_ROLE.ADMIN,
      ADMIN_ROLE.STATE_ADMIN,
      ADMIN_ROLE.PP_ADMIN,
    ],
    icon: FiGlobe,
  },
  {
    label: "VLE",
    path: PAGE_PATHS.VLE_STATS,
    roles: [
      ADMIN_ROLE.SUPER_ADMIN,
      ADMIN_ROLE.CENTRAL_ADMIN,
      ADMIN_ROLE.ADMIN,
      ADMIN_ROLE.STATE_ADMIN,
      ADMIN_ROLE.PP_ADMIN,
    ],
    icon: FiBarChart2,
  },
  {
    label: "Learner",
    path: PAGE_PATHS.LEARNER,
    roles: [
      ADMIN_ROLE.SUPER_ADMIN,
      ADMIN_ROLE.CENTRAL_ADMIN,
      ADMIN_ROLE.ADMIN,
      ADMIN_ROLE.STATE_ADMIN,
      ADMIN_ROLE.PP_ADMIN,
    ],
    icon: FiBookOpen,
  },
  {
    label: "Placement Partner",
    path: PAGE_PATHS.PLACEMENT_PARTNERS,
    roles: [
      ADMIN_ROLE.SUPER_ADMIN,
      ADMIN_ROLE.CENTRAL_ADMIN,
      ADMIN_ROLE.ADMIN,
    ],
    icon: FiMapPin,
  },
  {
    label: "Create Admin",
    path: PAGE_PATHS.ADMINS,
    roles: [ADMIN_ROLE.SUPER_ADMIN],
    icon: FiUsers,
  },
];

export default function Sidebar() {
  const { admin, role } = useAuth();

  const visibleItems = useMemo(() => {
    if (!role) return [];
    return MENU_ITEMS.filter((item) => item.roles.includes(role));
  }, [role]);

  const logout = useCallback(async () => {
    await services.requestSvc.post(
      "/logout",
      {},
      {
        withCredentials: true,
      },
    );
    window.location.reload();
  }, []);

  return (
    <aside className="w-64 h-full flex-shrink-0 min-h-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
      <div className="flex items-center gap-x-3 px-5 py-4 border-b border-gray-200">
        <img src={logo} alt="Digital Girls Hub" className="h-10 w-10" />
        <div className="flex flex-col">
          <span className="font-semibold text-sm">Digital Girls Hub</span>
          <span className="text-xs text-gray-500">{getRoleLabel(role)}</span>
        </div>
      </div>

      <div className="px-5 py-4 border-b border-gray-200">
        <div className="text-xs uppercase text-gray-400 tracking-wide">
          Signed In
        </div>
        <div className="mt-1 text-sm font-semibold text-gray-800">
          {admin?.name || "Admin"}
        </div>
        {admin?.email && (
          <div className="text-xs text-gray-500">{admin.email}</div>
        )}
      </div>

      <nav className="flex-1 min-h-0 px-3 py-4 flex flex-col gap-y-1 overflow-y-auto">
        {visibleItems.map((item) => {
          const ItemIcon = item.icon;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === PAGE_PATHS.COUNTRY_STATS}
              className={({ isActive }) =>
                [
                  "px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-x-3 transition-colors",
                  isActive
                    ? "bg-[#F4F0FF] text-[#6941C6]"
                    : "text-gray-700 hover:bg-gray-100",
                ].join(" ")
              }
            >
              <ItemIcon className="h-4 w-4 flex-shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="px-5 py-4 border-t border-gray-200">
        <button onClick={logout} className="btn-secondary w-full">
          Logout
        </button>
      </div>
    </aside>
  );
}
